import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PHASE_STATUS, statusClass, statusLabel } from "@/lib/workflow";

export const PhaseHeader = ({ project, phase, users, onChanged }) => {
  const [naOpen, setNaOpen] = useState(false);
  const [naReason, setNaReason] = useState("");
  const [saving, setSaving] = useState(false);

  const patch = async (body) => {
    setSaving(true);
    try {
      await api.patch(`/projects/${project.project_id}/phases/${phase.phase_id}`, body);
      await onChanged();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (v) => {
    if (v === "not_applicable") { setNaReason(phase.na_reason || ""); setNaOpen(true); return; }
    if (await patch({ status: v })) toast.success(`${phase.name} → ${statusLabel(v)}`);
  };

  const confirmNa = async () => {
    if (!naReason.trim()) return toast.error("Reason is required");
    if (await patch({ status: "not_applicable", na_reason: naReason.trim() })) { setNaOpen(false); toast.success("Phase marked Not Applicable"); }
  };

  return (
    <div className="flex flex-wrap items-start justify-between gap-4" data-testid="phase-header">
      <div className="min-w-0">
        <p className="overline">Phase {phase.order + 1} of 8</p>
        <h2 className="text-xl font-bold tracking-tight text-slate-900" data-testid="phase-title">{phase.name}</h2>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <Badge variant="outline" className={statusClass(phase.status)} data-testid="phase-status-badge">{statusLabel(phase.status)}</Badge>
          {phase.completion_date && <span>Completed <span className="font-mono">{phase.completion_date}</span></span>}
          {phase.status === "not_applicable" && phase.na_reason && <span className="italic">Reason: {phase.na_reason}</span>}
          {phase.history?.length > 0 && <span>· {phase.history.length} status change{phase.history.length > 1 ? "s" : ""}</span>}
          {phase.status === "completed" && phase.history?.[phase.history.length - 1]?.auto && <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700" data-testid="phase-auto-completed-badge">Completed automatically</span>}
        </div>
        {!phase.can_complete && phase.status !== "completed" && phase.status !== "not_applicable" && (
          <ul className="mt-3 space-y-0.5 text-xs text-amber-800" data-testid="phase-missing-list">
            {phase.missing.map((m, i) => <li key={i}>• {m}</li>)}
            {!phase.gate_ok && <li>• ERP Readiness Gate not passed — see checklist below</li>}
            <li className="text-slate-500">The phase completes automatically once every requirement above is satisfied.</li>
          </ul>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 w-full lg:w-auto">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Owner</Label>
          <Select value={phase.owner_id || ""} onValueChange={(v) => patch({ owner_id: v })} disabled={saving}>
            <SelectTrigger className="h-9 w-full sm:w-40" data-testid="phase-owner-select"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Status</Label>
          <Select value={phase.status} onValueChange={changeStatus} disabled={saving}>
            <SelectTrigger className="h-9 w-full sm:w-56" data-testid="phase-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PHASE_STATUS.filter((s) => s.value !== "not_applicable" || phase.allow_na).map((s) => (
                <SelectItem key={s.value} value={s.value} disabled={s.value === "completed" && !phase.can_complete}>
                  {s.label}{s.value === "completed" && !phase.can_complete ? " (requirements missing)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Dialog open={naOpen} onOpenChange={setNaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark "{phase.name}" as Not Applicable</DialogTitle></DialogHeader>
          <Label className="text-xs">Reason (required, recorded in history)</Label>
          <Textarea rows={3} value={naReason} onChange={(e) => setNaReason(e.target.value)} data-testid="phase-na-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNaOpen(false)}>Cancel</Button>
            <Button onClick={confirmNa} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="phase-na-confirm-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
